import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";
import {
  YOUTUBE_GET_LOADING,
  YOUTUBE_POST_LOADING,
  YOUTUBE_SYNC_LOADING,
} from "../../constants/loading.constant";
import { useGlobalCore } from "../globals";
import { Mentions } from "../socialPost/interfaces/Mention";
import { PostVideo } from "../socialPost/interfaces/postVideo";
import { dispatchOptimisticScheduledPostIfNeeded } from "../socialPost/helpers/scheduleResponseToSocialPost";
import { YoutubeSetting } from "./interface/youtubeSetting";
import { YoutubeDirectPostPayload } from "./helpers/publishValidation";
import { youtubeApiService } from "./services/api";
import { YoutubeAnalyticsSnapshot } from "./services/mockInsights";
import {
  bindYoutubeSuccessAction,
  getYoutubeInitAction,
  getYoutubeSuccessAction,
  resetYoutubeDirectPostDraftAction,
  setYoutubeDirectPostDraftAction,
  unbindYoutubeSuccessAction,
  YoutubeDirectPostDraft,
} from "./store/api-actions";
import {
  youtubeDirectPostDraftSelector,
  youtubeSettingsSelector,
} from "./store/selector";

function socialApiErrorMessage(error: unknown): string | undefined {
  const err = error as { response?: { data?: { message?: unknown } } };
  const data = err?.response?.data;
  if (!data || typeof data !== "object") return undefined;
  const m = (data as { message?: unknown }).message;
  if (typeof m === "string" && m.trim()) return m;
  if (Array.isArray(m)) {
    return m.filter((x) => typeof x === "string").join(", ");
  }
  return undefined;
}

export function useYoutubeCore() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const settings = useSelector(youtubeSettingsSelector);
  const directPostDraft = useSelector(youtubeDirectPostDraftSelector);
  const { turnOnLoading, turnOffLoading } = useGlobalCore();

  const get = useCallback(
    async (companyId: string) => {
      try {
        dispatch(getYoutubeInitAction());
        turnOnLoading(YOUTUBE_GET_LOADING);
        const response = await youtubeApiService.get(companyId);
        dispatch(getYoutubeSuccessAction({ settings: response }));
        turnOffLoading(YOUTUBE_GET_LOADING, 1500);
      } catch (error) {
        turnOffLoading(YOUTUBE_GET_LOADING);
        console.error(error);
        dispatch(getYoutubeSuccessAction({ settings: null }));
      }
    },
    [dispatch, turnOffLoading, turnOnLoading]
  );

  const bind = useCallback(
    async (companyId: string, code: string, onSuccess?: () => void) => {
      try {
        turnOnLoading(YOUTUBE_SYNC_LOADING);
        const response = await youtubeApiService.bind(companyId, { code });
        if (!response) {
          throw new Error("YouTube bind returned empty settings");
        }
        dispatch(bindYoutubeSuccessAction({ settings: response }));
        turnOffLoading(YOUTUBE_SYNC_LOADING, 500);
        toast.success(intl.formatMessage({ id: "app.youtube.bindSuccess" }), {
          toastId: "youtube-bind-result",
        });
        onSuccess?.();
      } catch (error) {
        turnOffLoading(YOUTUBE_SYNC_LOADING);
        toast.error(intl.formatMessage({ id: "app.youtube.bindError" }), {
          toastId: "youtube-bind-result",
        });
        console.error(error);
        throw error;
      }
    },
    [dispatch, intl, turnOffLoading, turnOnLoading]
  );

  const login = useCallback(
    async (companyId: string) => {
      try {
        turnOnLoading(YOUTUBE_SYNC_LOADING);
        const response = await youtubeApiService.login(companyId);
        turnOffLoading(YOUTUBE_SYNC_LOADING);
        return response.url;
      } catch (error) {
        turnOffLoading(YOUTUBE_SYNC_LOADING);
        toast.error(intl.formatMessage({ id: "app.youtube.loginError" }));
        console.error(error);
      }
    },
    [intl, turnOffLoading, turnOnLoading]
  );

  const unbind = useCallback(
    async (companyId: string, onSuccess?: () => void) => {
      try {
        turnOnLoading(YOUTUBE_SYNC_LOADING);
        await youtubeApiService.unbind(companyId);
        dispatch(unbindYoutubeSuccessAction());
        turnOffLoading(YOUTUBE_SYNC_LOADING, 500);
        toast.success(
          intl.formatMessage({ id: "app.youtube.unbindSuccess" })
        );
        onSuccess?.();
      } catch (error) {
        turnOffLoading(YOUTUBE_SYNC_LOADING);
        toast.error(intl.formatMessage({ id: "app.youtube.unbindError" }));
        console.error(error);
      }
    },
    [dispatch, intl, turnOffLoading, turnOnLoading]
  );

  const setDirectPostDraft = useCallback(
    (draft: Partial<YoutubeDirectPostDraft>) => {
      dispatch(setYoutubeDirectPostDraftAction({ draft }));
    },
    [dispatch]
  );

  const resetDirectPostDraft = useCallback(() => {
    dispatch(resetYoutubeDirectPostDraftAction());
  }, [dispatch]);

  const getInsights = useCallback(
    async (companyId: string, since: string, until: string) => {
      return youtubeApiService.getInsights(companyId, since, until);
    },
    []
  );

  const socialPostVideo = useCallback(
    async (
      companyId: string,
      text: string,
      mentions: Mentions[],
      video: PostVideo,
      scheduleDate: string,
      repostId: string,
      onSuccess?: () => void,
      youtubeDirectPost?: YoutubeDirectPostPayload | null,
      options?: { silent?: boolean }
    ) => {
      const silent = options?.silent === true;
      try {
        if (!silent) turnOnLoading(YOUTUBE_POST_LOADING);
        const data = await youtubeApiService.socialPostVideo(
          text,
          mentions,
          video,
          scheduleDate,
          repostId,
          companyId,
          youtubeDirectPost ?? undefined
        );
        if (!silent) turnOffLoading(YOUTUBE_POST_LOADING);
        if (!silent) {
          toast.success(
            scheduleDate
              ? intl.formatMessage({ id: "app.youtube.scheduleSuccess" })
              : intl.formatMessage({ id: "app.youtube.publishSuccess" })
          );
          if (!scheduleDate) {
            toast.info(
              "YouTube puede tardar unos minutos en procesar y mostrar el video en tu canal.",
              { autoClose: 7000 }
            );
          }
        }
        dispatchOptimisticScheduledPostIfNeeded(dispatch, data, {
          scheduleDate,
          companyId,
          defaultProvider: "youtube",
          postType: "video",
          text,
        });
        onSuccess?.();
      } catch (error: unknown) {
        if (!silent) turnOffLoading(YOUTUBE_POST_LOADING);
        if (!silent) {
          const fallback = scheduleDate
            ? intl.formatMessage({ id: "app.youtube.schedulePostError" })
            : intl.formatMessage({ id: "app.youtube.postError" });
          toast.error(socialApiErrorMessage(error) ?? fallback);
        }
        console.error(error);
        throw error;
      }
    },
    [dispatch, intl, turnOffLoading, turnOnLoading]
  );

  return {
    settings,
    directPostDraft,
    get,
    bind,
    login,
    unbind,
    setDirectPostDraft,
    resetDirectPostDraft,
    getInsights,
    socialPostVideo,
  };
}

export function isYoutubeConnected(
  settings: YoutubeSetting | null | undefined
): boolean {
  return Boolean(settings?.id || settings?.name);
}
